import ApiService, { ApiRequestOptions } from "./api";
import router from "@/router/index";
import { ref, computed } from "vue";
import { ApiError, toApiError } from "@/core/http/apiError";

export interface BaseCallResult<T> {
  isValid: boolean;
  model: T | null;
  error: ApiError | null;
}

/**
 * Real shape of every API Integrations v4 success response (confirmed live
 * against production - see docs/v4-migration/..., "Live Production
 * Verification"): the actual returned type is BaseResult<T>, serialized as
 * `{ model: T, correlationId, message }` - never the bare T itself. get()/
 * post() below unwrap this one level so every store's own `.model` is
 * already the real inner value (a WorkerNationality[], an IPageList<Worker>,
 * a RecruitmentRequestCreated, ...), matching what every store in this app
 * is actually written to expect. Do not remove this unwrap and start typing
 * base.get<T>()/base.post<T>() with the *outer* envelope shape instead - a
 * previous version of this file did exactly that (typed T as the inner
 * model but never unwrapped it), and every consumer silently read
 * `undefined` off the un-unwrapped envelope, or - for stores that spread the
 * result as an array (websiteContent.ts) - threw
 * "TypeError: result.model is not iterable" at runtime. That defect shipped
 * past a full production build (0 type errors) and manual live-API
 * contract review, because both only checked the JSON *shape*, never the
 * actual JS *access path* against it - a reminder that type-checking a
 * response body's fields is not the same as proving the code that reads it
 * is correct.
 */
interface BackendEnvelope<T> {
  model: T;
  correlationId?: string;
  message?: string;
}

export const useBaseStore = () => {
  const endPoint = ref("");
  const indexUrl = ref("");
  const loading = ref(false);
  /**
   * Normalized failure from the most recently completed request. Cleared at
   * the start of every get()/post() call so a stale error never survives
   * into a subsequent successful load. Views can render this directly
   * (loading / success / empty / error is the mandated pattern - see
   * docs/v4-migration/PAGE-COVERAGE.md) instead of the previous behavior of
   * silently console.error-ing and leaving the UI unchanged.
   */
  const error = ref<ApiError | null>(null);
  const language = computed(() => router.currentRoute.value.params.lang);
  const direction = computed(() => (language.value === "ar" ? "rtl" : "ltr"));

  function ApiEndPoint(endPointUrl: string, indexPageUrl?: string) {
    endPoint.value = endPointUrl;
    if (indexPageUrl) indexUrl.value = indexPageUrl;
  }

  function path(url: string): string {
    return url ? `${endPoint.value}/${url}` : endPoint.value;
  }

  async function get<T = unknown>(
    url: string,
    params?: object,
    signal?: AbortSignal
  ): Promise<BaseCallResult<T>> {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await ApiService.get<BackendEnvelope<T>>(path(url), params, signal);
      return { isValid: true, model: data.model, error: null };
    } catch (err) {
      const apiError = toApiError(err);
      if (apiError.kind === "cancelled") {
        // A superseded request (see requestCancellation.ts). Not a user
        // facing failure - do not populate `error` or log it.
        return { isValid: false, model: null, error: null };
      }
      error.value = apiError;
      // eslint-disable-next-line no-console
      console.error("[API] GET", path(url), apiError);
      return { isValid: false, model: null, error: apiError };
    } finally {
      loading.value = false;
    }
  }

  async function post<T = unknown>(
    url: string,
    data?: object,
    options?: ApiRequestOptions
  ): Promise<BaseCallResult<T>> {
    loading.value = true;
    error.value = null;
    try {
      const { data: responseData } = await ApiService.post<BackendEnvelope<T>>(
        path(url),
        data,
        options
      );
      return { isValid: true, model: responseData.model, error: null };
    } catch (err) {
      const apiError = toApiError(err);
      error.value = apiError;
      // eslint-disable-next-line no-console
      console.error("[API] POST", path(url), apiError);
      return { isValid: false, model: null, error: apiError };
    } finally {
      loading.value = false;
    }
  }

  return {
    ApiEndPoint,
    path,
    get,
    post,
    loading,
    error,
    language,
    direction,
  };
};
